import React, { useCallback, useMemo, useState } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { token } from '@atlaskit/tokens';
import { useFlagService } from '@atlassian/jira-flags/src/services';
import { useIntl } from '@atlassian/jira-intl';
import {
	FireScreenAnalytics,
	fireTrackAnalytics,
	useAnalyticsEvents,
	ContextualAnalyticsData,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import GenericError from '../../common/assets/GenericError.svg';
import { X2C_SCREENS } from '../../common/constants/metrics';
import type { X2CImportValidationError } from '../../common/types';
import { ErrorScreen } from '../../common/ui/error-screen';
import { StepFooterStandard } from '../../common/ui/step-footer';
import { ProgressBarStandard } from '../../common/ui/step-progress-bar';
import { StepScreen } from '../../common/ui/step-screen';
import { hasMissingFields, hasTableColumnErrors } from '../../common/utils';
import { useContentForCSV } from '../../common/utils/get-content-for-csv';
import { useAdvancedOptions, useMigrationId } from '../../controllers/import-state';
import {
	useImportType,
	useNumUnmappedColumnsIncluded,
	useProjectData,
	useSource,
} from '../../controllers/import-state/selectors';
import { useProductSteps } from '../../controllers/utils';
import { useValidateX2CImport } from '../../services/validate-import';
import { VALIDATION_FAILED_FLAG } from './constants';
import { FieldMapping } from './field-mapping';
import { MappingAdvancedOptions } from './mapping-advanced-options';
import messages from './messages';
import { UnmappedColumnsWarningModal } from './unmapped-columns-warning-modal';
import { useMappings } from './use-mappings';
import { ValidationError } from './validation-error';

export type CSVFieldMappingScreenProps = {
	onNext: () => void;
	onReturn: () => void;
	onCloseAndReturnToUploadStep: () => void;
	isCompact?: boolean;
};

export const CSVFieldMappingScreen = ({
	onNext,
	onReturn,
	onCloseAndReturnToUploadStep,
	isCompact,
}: CSVFieldMappingScreenProps) => {
	const [isUnmappedWarningModalOpen, setIsUnmappedWarningModalOpen] = useState(false);
	const { formatMessage } = useIntl();
	const {
		isLoading: isPreviewLoading,
		hasErrorOccurred,
		onRefresh,
		mappingState,
		setMappingStateValidationError,
		setUnmappedColumnsNotIncluded,
	} = useMappings();
	const [migrationId] = useMigrationId();
	const [advancedOptions] = useAdvancedOptions();
	const { showFlag } = useFlagService();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { productSteps, currentStepIndex } = useProductSteps();

	const [source] = useSource();
	const [serviceName] = useImportType();
	const [{ type }] = useProjectData();
	const { isLoading, validateX2CImport } = useValidateX2CImport(source, serviceName);
	const { fieldMappingDescription } = useContentForCSV(serviceName, type);
	const [numOfUnmappedColumnsIncluded] = useNumUnmappedColumnsIncluded();

	const onCloseUnmappedWarningModal = useCallback(() => {
		setIsUnmappedWarningModalOpen(false);
	}, []);

	const handleOnNext = useCallback(() => {
		setUnmappedColumnsNotIncluded();
		onNext();
	}, [onNext, setUnmappedColumnsNotIncluded]);

	const onNextOrOpenWarningModal = useCallback(() => {
		numOfUnmappedColumnsIncluded === 0 ? handleOnNext() : setIsUnmappedWarningModalOpen(true);
	}, [handleOnNext, numOfUnmappedColumnsIncluded]);

	const onValidateFetchFail = useCallback(() => {
		showFlag(VALIDATION_FAILED_FLAG);
	}, [showFlag]);

	const onValidationError = useCallback(
		(err: X2CImportValidationError) => {
			setMappingStateValidationError(err);
			fireTrackAnalytics(createAnalyticsEvent({}), 'columnMappingValidation failed', {
				numberOfColumnErrors: Object.keys(err.columnErrors).length,
				missingFields: err.missingFields,
			});
		},
		[createAnalyticsEvent, setMappingStateValidationError],
	);

	const validateOnContinue = useCallback(() => {
		if (mappingState === null || migrationId === null) {
			return;
		}
		validateX2CImport({
			migrationId,
			mappingState,
			advancedOptions,
			onSuccess: onNextOrOpenWarningModal,
			onFetchFail: onValidateFetchFail,
			onValidationError,
		});
	}, [
		mappingState,
		migrationId,
		validateX2CImport,
		advancedOptions,
		onNextOrOpenWarningModal,
		onValidateFetchFail,
		onValidationError,
	]);

	const hasValidationErrors = hasTableColumnErrors(mappingState) || hasMissingFields(mappingState);

	const sectionMessage = useMemo(
		() => (
			<Box paddingBlockEnd="space.200">
				{!hasValidationErrors &&
					(!mappingState || mappingState.validationError === null ? (
						<SectionMessage title={formatMessage(messages.summaryRequiredTitle)}>
							<Box as="p">{formatMessage(messages.summaryRequiredMessage)}</Box>
						</SectionMessage>
					) : (
						<SectionMessage title={formatMessage(messages.postValidationBannerHeading)}>
							<Box as="p">{formatMessage(messages.postValidationBannerDescription)}</Box>
						</SectionMessage>
					))}
			</Box>
		),
		[formatMessage, hasValidationErrors, mappingState],
	);

	return (
		<ContextualAnalyticsData sourceName={X2C_SCREENS.MAPPING} sourceType={SCREEN}>
			<StepScreen
				renderProgressBar={
					!isCompact && (
						<ProgressBarStandard
							productSteps={productSteps}
							currentStepIndex={currentStepIndex}
							onClose={onCloseAndReturnToUploadStep}
						/>
					)
				}
				renderFooter={
					<StepFooterStandard
						alignRight
						isFixedPosition={!isCompact}
						onReturn={onReturn}
						isLoading={isLoading}
						shouldDisableNext={mappingState === null || hasValidationErrors}
						nextButtonType="submit"
						onClickNext={validateOnContinue}
					/>
				}
			>
				<Box xcss={headerWrapperStyles}>
					<Box xcss={headerStyles}>{formatMessage(messages.csvFieldMappingHeading)}</Box>
					{!hasErrorOccurred && (
						<>
							<Box xcss={descriptionStyles}>{fieldMappingDescription}</Box>
							{sectionMessage}
						</>
					)}
				</Box>
				{!hasErrorOccurred && (
					<>
						{hasValidationErrors && <ValidationError />}
						<MappingAdvancedOptions />
						<FieldMapping isLoading={isPreviewLoading} />
					</>
				)}
				{hasErrorOccurred && (
					<ErrorScreen
						onClick={onRefresh}
						illustration={GenericError}
						header={formatMessage(messages.networkErrorHeading)}
						description={formatMessage(messages.networkErrorDescription)}
					/>
				)}
				<UnmappedColumnsWarningModal
					isModalOpen={isUnmappedWarningModalOpen}
					onCancel={onCloseUnmappedWarningModal}
					onContinue={handleOnNext}
					offsetModalY={64}
				/>
				<FireScreenAnalytics />
			</StepScreen>
		</ContextualAnalyticsData>
	);
};

const headerWrapperStyles = xcss({
	width: '610px',
	marginInline: 'space.200',
	alignSelf: 'center',
});

const headerStyles = xcss({
	color: 'color.text',
	font: token('font.heading.large'),
	paddingBottom: 'space.100',
});

const descriptionStyles = xcss({
	color: 'color.text.subtle',
	font: token('font.body'),
	paddingBottom: 'space.200',
});
